import { ReactElement, useEffect, useState, SyntheticEvent } from 'react';

import { getDownloadURL, ref } from 'firebase/storage';

import { useNavigate } from 'react-router-dom';

import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';

import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

import { useTheme, keyframes } from '@mui/material';

import GratiPageWrapper from '../components/common/GratiPageWrapper';
import { useApp } from '../contextProviders/AppProvider';
import { ActionProps } from '../contextProviders/AppProvider';
import { imagesRef } from '../firebase';
import { HANDICAP_LABELS } from '../utils/handicapUtils';

const bounce = keyframes`
  0%, 20%, 50%, 80%, 100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-10px);
  }
  60% {
    transform: translateY(-5px);
  }
`;

export default function WelcomePage(): ReactElement {
  const welcomeImageFileName = 'welcome.png';
  const [welcomeImageUrl, setWelcomeImageUrl] = useState('');
  const [expandedAccordion, setExpandedAccordion] = useState<string | false>(false);
  const [expandedOfferAccordion, setExpandedOfferAccordion] = useState<string | false>(false);
  const navigate = useNavigate();
  const theme = useTheme();
  const isExtraSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const { setPageName, updateActions } = useApp();

  useEffect(() => {
    setPageName(null);
  }, [setPageName]);

  useEffect(() => {
    getDownloadURL(ref(imagesRef, welcomeImageFileName))
      .then((url) => {
        setWelcomeImageUrl(url);
      })
      .catch((error) => {
        console.log(error);
      });
    const actions: ActionProps[] = [
      {
        name: 'Log in',
        handleClick: () => {
          navigate('/login');
        },
        variant: 'outlined',
      },
    ];

    // Only add "New to Gratigolf" button if not on extra small screen
    if (!isExtraSmallScreen) {
      actions.unshift({
        name: 'New to Gratigolf',
        handleClick: () => {
          navigate('/accept-org-invitation');
        },
        variant: 'contained',
      });
    }

    updateActions(actions);
  }, [navigate, updateActions, isExtraSmallScreen]);

  const handleAccordionChange = (panel: string) => (event: SyntheticEvent, isExpanded: boolean) => {
    setExpandedAccordion(isExpanded ? panel : false);
  };

  const handleOfferAccordionChange = (panel: string) => (event: SyntheticEvent, isExpanded: boolean) => {
    setExpandedOfferAccordion(isExpanded ? panel : false);
  };

  return (
    <GratiPageWrapper isAuthRequired={false} isNoAuthRequired={true}>
      <Box
        sx={{
          height: '100vh',
          overflowY: 'auto',
          scrollSnapType: 'y mandatory',
          scrollBehavior: 'smooth',
          '&::-webkit-scrollbar': {
            display: 'none',
          },
          scrollbarWidth: 'none',
          position: 'relative',
        }}
      >
        {/* Panel 1: Original Hero Section */}
        <Stack
          spacing={1}
          sx={{
            minHeight: '100vh',
            scrollSnapAlign: 'start',
            scrollSnapStop: 'always',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'surface.main',
            minWidth: (theme) => theme.breakpoints.values.xs,
            py: 2,
            position: 'relative',
          }}
        >
          <Typography 
            gutterBottom 
            variant={isExtraSmallScreen ? "displaySmall" : "displayMedium"} 
            component="h3" 
            align="center">
            Fewer keyboard taps,
            <br />
            more golf claps.
          </Typography>
          <Typography
            variant="bodyMedium"
            align="center"
            sx={{
              color: "surface.contrastText",
              maxWidth: 'sm'
            }}>
            Gratigolf is the refreshingly simple app for managing your leagues, tournaments, and events. We handle the
            hard work from signup through posted results — so you and your players can focus on what matters: creating
            unforgettable experiences on the course. It&apos;s all the power you need, without the hassle, from a
            computer, tablet or phone.
          </Typography>
          <Stack
            direction="row"
            spacing={1}
            sx={{
              width: '100%',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Button variant="contained" href="/accept-org-invitation" size="small">
              New to Gratigolf
            </Button>
            <Button variant="outlined" href="/login" size="small">
              Log in
            </Button>
          </Stack>
          <Box
            sx={{
              overflow: 'hidden',
              maxWidth: (theme) => theme.breakpoints.values.sm,
              width: 'calc(100% - 2rem)',
            }}
          >
            {welcomeImageUrl ? (
              <img
                src={welcomeImageUrl}
                alt="Welcome"
                style={{
                  width: '100%',
                  height: 'auto',
                  display: 'block',
                  opacity: 0.7,
                  filter: 'brightness(1.05)',
                }}
              />
            ) : (
              <Box sx={{ width: '100%', height: 'auto', display: 'block', opacity: 0.7, filter: 'brightness(1.05)' }} />
            )}
          </Box>
          
          {/* Scroll indicator */}
          <Box
            sx={{
              position: 'absolute',
              bottom: 24,
              left: '50%',
              transform: 'translateX(-50%)',
              animation: `${bounce} 2s infinite`,
              cursor: 'pointer',
              opacity: 0.7,
              '&:hover': {
                opacity: 1,
              },
            }}
            onClick={() => {
              window.scrollTo({
                top: window.innerHeight,
                behavior: 'smooth',
              });
            }}
          >
            <KeyboardArrowDownIcon sx={{ fontSize: 40, color: 'text.secondary' }} />
          </Box>
        </Stack>

        {/* Panel 2: Curious about Gratigolf */}
        <Box
          sx={{
            minHeight: '100vh',
            scrollSnapAlign: 'start',
            scrollSnapStop: 'always',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'background.paper',
            px: 1,
          }}
        >
          <Box sx={{ width: '100%', maxWidth: 'lg' }}>
            <Typography 
              variant="displaySmall" 
              component="h2" 
              align="center" 
              gutterBottom
            >
              Curious about
              <br />
              Gratigolf?
            </Typography>
            
            <Typography
              variant="bodyLarge"
              align="center"
              sx={{
                color: "text.secondary",
                maxWidth: 'sm',
                mx: 'auto'
              }}>
              Learn how Gratigolf helps players, pros, and clubs have a
              better experience, on and off the course.
            </Typography>

            <Stack spacing={2} sx={{ maxWidth: 'md', mx: 'auto' }}>
              <Accordion expanded={expandedAccordion === 'golfers'} onChange={handleAccordionChange('golfers')}>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="invited-golfers-content"
                  id="invited-golfers-header"
                >
                  <Typography variant="headlineSmall">Golfers</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="bodyMedium" sx={{
                    color: "text.secondary"
                  }}>
                    Sign up for events, view tee times, see results, and connect with fellow golfers—all in one place.
                  </Typography>
                  <Button 
                    variant="contained" 
                    sx={{ mt: 2 }} 
                    href="/accept-org-invitation"
                    endIcon={<ArrowForwardIcon />}
                  >
                    Apply Now
                  </Button>
                </AccordionDetails>
              </Accordion>

              <Accordion expanded={expandedAccordion === 'golf-pros'} onChange={handleAccordionChange('golf-pros')}>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="golf-pros-content"
                  id="golf-pros-header"
                >
                  <Typography variant="headlineSmall">Golf Pros</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="bodyMedium" sx={{
                    color: "text.secondary"
                  }}>
                    Create and manage events from signups through posting results. It&apos;s just as easy to manage a league as it is to manage a one day tournament.
                  </Typography>
                  <Button 
                    variant="contained" 
                    sx={{ mt: 2 }} 
                    href="/accept-org-invitation"
                    endIcon={<ArrowForwardIcon />}
                  >
                    Apply Now
                  </Button>
                </AccordionDetails>
              </Accordion>

              <Accordion expanded={expandedAccordion === 'club-managers'} onChange={handleAccordionChange('club-managers')}>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="club-managers-content"
                  id="club-managers-header"
                >
                  <Typography variant="headlineSmall">Club Managers</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="bodyMedium" sx={{
                    color: "text.secondary"
                  }}>
                    Simplify member management and communication. Keep your members informed about upcoming events through messaging and notifications.
                  </Typography>
                  <Button 
                    variant="contained" 
                    sx={{ mt: 2 }} 
                    href="/accept-org-invitation"
                    endIcon={<ArrowForwardIcon />}
                  >
                    Apply Now
                  </Button>
                </AccordionDetails>
              </Accordion>
            </Stack>
          </Box>
        </Box>

        {/* Panel 3: Who We Are */}
        <Box
          sx={{
            minHeight: '100vh',
            scrollSnapAlign: 'start',
            scrollSnapStop: 'always',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'surface.main',
            padding: 3,
          }}
        >
          <Box sx={{ width: '100%', maxWidth: 'lg' }}>
            <Typography 
              variant="displaySmall" 
              component="h2" 
              align="center" 
              gutterBottom
            >
              Who we are
            </Typography>
            
            <Typography
              variant="bodyLarge"
              align="center"
              sx={{
                color: "text.secondary",
                mb: 4,
                maxWidth: 'sm',
                mx: 'auto'
              }}>
              We love golf!<br />
              We adhere to four principles:
            </Typography>

            <Grid 
              container 
              spacing={{ xs: 2, md: 3 }} 
              sx={{ maxWidth: 'lg', mx: 'auto', mb: 4 }}
            >
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Card 
                  sx={{ 
                    height: '100%',
                    backgroundColor: 'primary.main',
                    color: 'primary.contrastText'
                  }}
                >
                  <CardContent sx={{ textAlign: 'center', p: 2 }}>
                    <Typography variant="titleMedium" gutterBottom>
                       Delight
                    </Typography>
                    <Typography variant="bodySmall">
                      Running a league or event should not just be hassle-free, it should be fun! 
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Card 
                  sx={{ 
                    height: '100%',
                    backgroundColor: 'secondary.main',
                    color: 'secondary.contrastText'
                  }}
                >
                  <CardContent sx={{ textAlign: 'center', p: 2 }}>
                    <Typography variant="titleMedium" gutterBottom>
                      Fun
                    </Typography>
                    <Typography variant="bodySmall">
                      Signing up, showing up, playing and competing should always be fun. 
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Card 
                  sx={{ 
                    height: '100%',
                    backgroundColor: 'tertiary.main',
                    color: 'tertiary.contrastText'
                  }}
                >
                  <CardContent sx={{ textAlign: 'center', p: 2 }}>
                    <Typography variant="titleMedium" gutterBottom>
                      Connection
                    </Typography>
                    <Typography variant="bodySmall">
                      When we know are connected, experiences are richer. 
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Card 
                  sx={{ 
                    height: '100%',
                    backgroundColor: 'error.main',
                    color: 'error.contrastText'
                  }}
                >
                  <CardContent sx={{ textAlign: 'center', p: 2 }}>
                    <Typography variant="titleMedium" gutterBottom>
                      Community
                    </Typography>
                    <Typography variant="bodySmall">
                      We give back by sponsoring youth clinics and events to welcome new players to the sport.
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            <Box sx={{ textAlign: 'center' }}>
              <Button 
                variant="contained" 
                size="medium" 
                href="/accept-org-invitation"
                endIcon={<ArrowForwardIcon />}
              >
                Apply Now
              </Button>
            </Box>
          </Box>
        </Box>

        {/* Panel 4: What We Offer */}
        <Box
          sx={{
            minHeight: '100vh',
            scrollSnapAlign: 'start',
            scrollSnapStop: 'always',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'background.paper',
            padding: 3,
          }}
        >
          <Box sx={{ width: '100%', maxWidth: 'lg', mx: 'auto' }}>
            <Typography 
              variant="displaySmall" 
              component="h2" 
              align="center" 
              gutterBottom
              sx={{ mb: 2 }}
            >
              What We Offer
            </Typography>
            
            <Typography
              variant="bodyLarge"
              align="center"
              sx={{
                color: "text.secondary",
                mb: 4,
                maxWidth: 'sm',
                mx: 'auto'
              }}>
              Learn how Gratigolf helps players, pros, and clubs have a better experience, on and off the course.
            </Typography>

            <Stack spacing={2} sx={{ maxWidth: 'md', mx: 'auto' }}>
              <Accordion expanded={expandedOfferAccordion === 'league-management'} onChange={handleOfferAccordionChange('league-management')}>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="league-management-content"
                  id="league-management-header"
                >
                  <Typography variant="headlineSmall">League Management</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="bodyMedium" sx={{
                    color: "text.secondary"
                  }}>
                    Manage your league from start to finish with automated pairings, {HANDICAP_LABELS.INDEX} tracking, and season standings.
                  </Typography>
                  <Button 
                    variant="contained" 
                    sx={{ mt: 2 }} 
                    href="/accept-org-invitation"
                    endIcon={<ArrowForwardIcon />}
                  >
                    Learn More
                  </Button>
                </AccordionDetails>
              </Accordion>

              <Accordion expanded={expandedOfferAccordion === 'event-management'} onChange={handleOfferAccordionChange('event-management')}>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="event-management-content"
                  id="event-management-header"
                >
                  <Typography variant="headlineSmall">Event Management</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="bodyMedium" sx={{
                    color: "text.secondary"
                  }}>
                    Create tournaments and events with custom formats, registration management, and automated results posting.
                  </Typography>
                  <Button 
                    variant="contained" 
                    sx={{ mt: 2 }} 
                    href="/accept-org-invitation"
                    endIcon={<ArrowForwardIcon />}
                  >
                    Learn More
                  </Button>
                </AccordionDetails>
              </Accordion>

              <Accordion expanded={expandedOfferAccordion === 'member-management'} onChange={handleOfferAccordionChange('member-management')}>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="member-management-content"
                  id="member-management-header"
                >
                  <Typography variant="headlineSmall">Member Management</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="bodyMedium" sx={{
                    color: "text.secondary"
                  }}>
                    Keep your members engaged with communication tools, notifications, and easy access to club information.
                  </Typography>
                  <Button 
                    variant="contained" 
                    sx={{ mt: 2 }} 
                    href="/accept-org-invitation"
                    endIcon={<ArrowForwardIcon />}
                  >
                    Learn More
                  </Button>
                </AccordionDetails>
              </Accordion>
            </Stack>
          </Box>
        </Box>

        {/* Panel 5: Footer */}
        <Box
          sx={{
            minHeight: '100vh',
            scrollSnapAlign: 'start',
            scrollSnapStop: 'always',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'surface.main',
            padding: 3,
          }}
        >
          <Box 
            component="footer" 
            sx={{ 
              width: '100%',
              maxWidth: 'lg',
              mx: 'auto',
              textAlign: 'center'
            }}
          >
            <Grid container spacing={4} sx={{
              justifyContent: "center"
            }}>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Typography variant="labelSmall" gutterBottom sx={{
                  color: "text.secondary"
                }}>
                  Gratigolf
                </Typography>
                <Typography variant="bodySmall" sx={{
                  color: "text.secondary"
                }}>
                  © {new Date().getFullYear()} Gratisports, Inc. All Rights Reserved
                </Typography>
              </Grid>
              
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Stack spacing={1}>
                  <Link href="/login" variant="bodySmall" sx={{
                    color: "text.secondary"
                  }}>
                    Log In
                  </Link>
                  <Link href="/accept-org-invitation" variant="bodySmall" sx={{
                    color: "text.secondary"
                  }}>
                    Request a Demo
                  </Link>
                  <Link href="#" variant="bodySmall" sx={{
                    color: "text.secondary"
                  }}>
                    Contact Us
                  </Link>
                </Stack>
              </Grid>
              
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Typography variant="labelSmall" gutterBottom sx={{
                  color: "text.secondary"
                }}>
                  LEGAL
                </Typography>
                <Stack spacing={1}>
                  <Link href="/privacy" variant="bodySmall" sx={{
                    color: "text.secondary"
                  }}>
                    Privacy Policy
                  </Link>
                  <Link href="/terms" variant="bodySmall" sx={{
                    color: "text.secondary"
                  }}>
                    Terms of Service
                  </Link>
                  <Link href="/copyright" variant="bodySmall" sx={{
                    color: "text.secondary"
                  }}>
                    Copyright Policy
                  </Link>
                </Stack>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Box>
    </GratiPageWrapper>
  );
}
